import { z } from 'zod';
import { queueViewSchema } from './types';

// Maintenance, audit log and admin users, from social-admin/docs/api.md.

// --- Maintenance -----------------------------------------------------------

export const subscriptionViewSchema = z.object({
  id: z.string(),
  owner_scope: z.string(),
  partner_id: z.string().nullable(),
  org_id: z.string().nullable(),
  host: z.string(),
  event_types: z.array(z.string()),
  status: z.string(),
  recent_failures: z.number(),
  pending: z.number(),
  dead: z.number(),
  created_at: z.string(),
});
export type SubscriptionView = z.infer<typeof subscriptionViewSchema>;

export const purgeRunSchema = z.object({
  id: z.string(),
  job: z.string(),
  cutoff: z.string(),
  deleted_count: z.number(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  succeeded: z.boolean(),
  error: z.string().nullable(),
});
export type PurgeRun = z.infer<typeof purgeRunSchema>;

export const maintenanceOverviewSchema = z.object({
  stale_after: z.string(),
  queues: z.array(queueViewSchema),
  dead_deliveries: z.number(),
  reauth: z.object({ total: z.number(), by_platform: z.record(z.string(), z.number()) }),
  failure_window: z.string(),
  unhealthy_subscriptions: z.array(subscriptionViewSchema),
  retention: z.array(
    z.object({
      store: z.string(),
      label: z.string(),
      kept_for: z.string(),
      cutoff: z.string(),
      job: z.string(),
    }),
  ),
  last_purge_runs: z.array(purgeRunSchema),
});
export type MaintenanceOverview = z.infer<typeof maintenanceOverviewSchema>;

export const releaseResultSchema = z.object({
  queue: z.string(),
  label: z.string(),
  released: z.number(),
  lease: z.string(),
});
export type ReleaseResult = z.infer<typeof releaseResultSchema>;

export const needsReauthSchema = z.object({
  social_account_id: z.string(),
  org_id: z.string(),
  platform: z.string(),
  since: z.string(),
});
export type NeedsReauthAccount = z.infer<typeof needsReauthSchema>;

export const vaultSchema = z.object({
  current_key_version: z.string(),
  rotatable_key_versions: z.array(z.string()),
});
export type Vault = z.infer<typeof vaultSchema>;

export const rotationSchema = z.object({
  from_key_version: z.string(),
  to_key_version: z.string(),
  platform_tokens: z.number(),
  subscription_secrets: z.number(),
  admin_totp_secrets: z.number(),
  total: z.number(),
  complete: z.boolean(),
});
export type Rotation = z.infer<typeof rotationSchema>;

// --- Keyset pages ------------------------------------------------------------

export function keysetSchema<Item extends z.ZodType>(item: Item) {
  return z.object({
    items: z.array(item),
    next: z.object({ after: z.string(), after_id: z.string() }).nullable(),
    has_more: z.boolean(),
  });
}
export type KeysetPage<T> = {
  items: T[];
  next: { after: string; after_id: string } | null;
  has_more: boolean;
};

// --- Audit -------------------------------------------------------------------

export const auditEntrySchema = z.object({
  id: z.string(),
  created_at: z.string(),
  actor_type: z.string(),
  actor_id: z.string().nullable(),
  actor_email: z.string().nullable(),
  partner_id: z.string().nullable(),
  org_id: z.string().nullable(),
  action: z.string(),
  target_type: z.string().nullable(),
  target_id: z.string().nullable(),
  detail: z.record(z.string(), z.unknown()).nullable(),
  ip: z.string().nullable(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

// --- Admin users -------------------------------------------------------------

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(['SUPER_ADMIN', 'OPERATIONS', 'BILLING', 'READ_ONLY']),
  status: z.enum(['ACTIVE', 'DISABLED']),
  locked: z.boolean(),
  locked_until: z.string().nullable(),
  two_factor: z.boolean(),
  must_change_password: z.boolean(),
  last_login_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  self: z.boolean(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const withPasswordSchema = z.object({
  admin_user: adminUserSchema,
  one_time_secret: z.string(),
});
export type WithPassword = z.infer<typeof withPasswordSchema>;
